import React from "react";
import PillarFormDateTimeInput from "shared/components/pillar-form/components/PillarFormDateTimeInput";
import PillarFormTextInput from "shared/components/pillar-form/components/PillarFormTextInput";
import { type PillarFormInputComponentConfig } from "shared/components/pillar-form/PillarFormInputComponentConfig";

export const FormInputTypes = {
  Text: "Text",
  DateTime: "DateTime",
} as const;

// Allow any for form component props mapping - components have different props
export const PillarFormDynamicInputComponentsMapper = {
  [FormInputTypes.Text]: PillarFormTextInput,
  [FormInputTypes.DateTime]: PillarFormDateTimeInput,
} as const;

// SurveyJS-like config shape
type InternalFormInputConfig<
  T extends keyof typeof PillarFormDynamicInputComponentsMapper,
> = React.ComponentProps<(typeof PillarFormDynamicInputComponentsMapper)[T]> & {
  // surveyjs-like properties
  type: T; // maps to one of FormInputTypes keys
  inputType?: string; // e.g. "text", "number", "date", "datetime-local"
  name: string; // formik field name
  title?: string; // surveyjs `title` (label)
};

export type FormInputConfig = {
  [K in keyof typeof PillarFormDynamicInputComponentsMapper]: InternalFormInputConfig<K>;
}[keyof typeof PillarFormDynamicInputComponentsMapper];

interface PillarFormDynamicInputComponentProps {
  element: PillarFormInputComponentConfig;
  className?: string;
}

export const PillarFormDynamicInputComponent: React.FC<
  PillarFormDynamicInputComponentProps
> = ({ element, className = "w-full mb-1" }) => {
  // Normalize element type to determine component
  const type = String(element.type || "").toLowerCase();
  const inputType = (element.inputType || "").toLowerCase();

  // Determine which component to render
  const shouldRenderDateTime =
    type.indexOf("date") >= 0 || inputType === "date";

  // Common props for all components
  const commonProps = {
    name: element.name,
    label: element.title ?? element.name,
    testid: element.testid,
  };

  if (shouldRenderDateTime) {
    // Determine date/time type
    let dateTimeType: "date" | "time" | "datetime-local" = "datetime-local";
    if (inputType === "date") {
      dateTimeType = "date";
    } else if (inputType === "time") {
      dateTimeType = "time";
    }

    return (
      <div className={className}>
        <PillarFormDynamicInputComponentsMapper.DateTime
          {...commonProps}
          dateTimeType={dateTimeType}
        />
      </div>
    );
  }

  // Default to text input
  return (
    <div className={className}>
      <PillarFormDynamicInputComponentsMapper.Text {...commonProps} />
    </div>
  );
};

// Helper function to render dynamic form components (deprecated - use PillarFormDynamicInputComponent instead)
export const renderDynamicFormComponent = (
  element: PillarFormInputComponentConfig,
  key?: string | number,
): React.ReactElement | null => {
  // normalize element type to the mapper keys
  const type = String(element.type || "").toLowerCase();
  let componentKey: keyof typeof PillarFormDynamicInputComponentsMapper | null =
    null;

  if (type === "text") {
    componentKey = "Text";
  }
  if (type.indexOf("date") >= 0 || (element.inputType || "") === "date") {
    componentKey = "DateTime";
  }

  if (!componentKey) {
    return null;
  }

  // props for date/time inputs
  const dateTimeProps: Record<string, string> = {};
  if (componentKey === "DateTime") {
    const inputType = (element.inputType || "").toLowerCase();
    if (inputType === "date") {
      dateTimeProps.dateTimeType = "date";
    } else if (inputType === "time") {
      dateTimeProps.dateTimeType = "time";
    } else {
      dateTimeProps.dateTimeType = "datetime-local";
    }
  }

  return (
    <div key={key ?? element.name} className="w-full mb-1">
      {componentKey === "DateTime"
        ? React.createElement(PillarFormDynamicInputComponentsMapper.DateTime, {
            name: element.name,
            label: (element.title as string) ?? element.name,
            testid: element.testid,
            dateTimeType:
              (dateTimeProps.dateTimeType as
                | "date"
                | "time"
                | "datetime-local") || "datetime-local",
          })
        : React.createElement(PillarFormDynamicInputComponentsMapper.Text, {
            name: element.name,
            label: (element.title as string) ?? element.name,
            testid: element.testid,
          })}
    </div>
  );
};

export default PillarFormDynamicInputComponentsMapper;
